import { computed, inject, Injectable } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';

import { TellentAppsDataService } from '@core/tellent-data';
import { TellentCurrentUserDataFacade } from '@core/tellent-logic';
import { AppKind, injectTellentAppKind, SeatKind } from '@core/tellent-types';
import { injectTellentAppUrlOpener } from '@core/tellent-util';
import { WindowRef } from '@core/user-interface-legacy';

import { injectManageFeaturesPromotionFlag } from './manage-features-promotion-flag.provider';
import { ProductsPromoAnalytics } from './products-promo.analytics';

@Injectable({ providedIn: 'root' })
export class ProductsPromoService {
  private readonly tellentAppsService = inject(TellentAppsDataService);
  private readonly tellentCurrentUserDataFacade = inject(TellentCurrentUserDataFacade);
  private readonly tellentAppUrlOpener = injectTellentAppUrlOpener('_blank');
  private readonly winRef = inject(WindowRef);
  private readonly router = inject(Router);
  private readonly productsPromoAnalytics = inject(ProductsPromoAnalytics);

  public readonly hasManageFeaturesPromotion =
    (injectTellentAppKind() === AppKind.Recruitee &&
      injectManageFeaturesPromotionFlag({ optional: true })) ??
    false;

  public hasActiveTellentHrSub = toSignal(
    this.tellentAppsService.hasActiveSubscriptionAccess(SeatKind.CoreHR),
  );

  public hasActiveTellentHrTrial = toSignal(
    this.tellentAppsService.hasActiveTrialAccess(SeatKind.CoreHR),
  );

  public hasActiveTellentHrSubOrTrial = computed(
    () => this.hasActiveTellentHrTrial() || this.hasActiveTellentHrSub(),
  );

  public hasJourneys = computed(
    () =>
      !!this.tellentCurrentUserDataFacade
        .seatByKind(SeatKind.CoreHR)()
        ?.app.subscriptionItems?.find(item => item.id.includes('tellent_hr_journeys')),
  );

  public hasAnyTellentHrFeature = computed(
    () => this.hasActiveTellentHrSubOrTrial() || this.hasJourneys(),
  );

  public bookDemo(viewContext: 'congratsOnHire' | 'offerPromote' | 'marketplace'): void {
    this.winRef.open('https://tellent.com/demo', '_blank');
    this.productsPromoAnalytics.trackBookADemoClick(viewContext);
  }

  public openTellentHr(): void {
    this.tellentAppUrlOpener(AppKind.KiwiHr);
  }

  public openMarketplaceDialog(page: 'settings' | 'description'): void {
    this.router.navigate([{ outlets: { dialog: ['integration', 'kiwi_hr', page] } }], {
      queryParamsHandling: 'preserve',
    });
  }
}
